import { useEffect, useMemo, useState } from "react";
import { fetchConfidenceCalibration } from "@/api/client";
import type { AiDecisionFeedItem, ConfidenceBucketDto, TechnicalErrorDto } from "@/api/types";

interface ObservabilityPanelProps {
  decisions: AiDecisionFeedItem[];
  errors: TechnicalErrorDto[];
}

function classifyGate(reason: string): string {
  const r = reason.toLowerCase();
  if (r.includes("correlated")) return "Correlation exposure";
  if (r.includes("higher-timeframe") || r.includes("higher timeframe")) return "Multi-timeframe";
  if (r.includes("heat") || r.includes("exposure")) return "Portfolio heat";
  if (r.includes("drawdown") || r.includes("daily loss") || r.includes("kill switch")) return "Kill switch";
  if (r.includes("score")) return "Score floor";
  return "Other";
}

export default function ObservabilityPanel({ decisions, errors }: ObservabilityPanelProps) {
  const [calibration, setCalibration] = useState<ConfidenceBucketDto[] | null>(null);
  const [calibrationError, setCalibrationError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchConfidenceCalibration()
      .then((res) => {
        if (!cancelled) setCalibration(res.calibration);
      })
      .catch((err) => {
        if (!cancelled) setCalibrationError(err instanceof Error ? err.message : "Failed to load");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rejections = useMemo(() => {
    const rejected = decisions.filter((d) => d.riskApproved === false && d.riskReason);
    const byGate = new Map<string, number>();
    for (const d of rejected) {
      const gate = classifyGate(d.riskReason as string);
      byGate.set(gate, (byGate.get(gate) ?? 0) + 1);
    }
    return {
      total: rejected.length,
      byGate: Array.from(byGate.entries()).sort((a, b) => b[1] - a[1]),
    };
  }, [decisions]);

  const errorRate = useMemo(() => {
    const errorCount = errors.filter((e) => e.level === "error").length;
    const warnCount = errors.filter((e) => e.level === "warn").length;
    return { errorCount, warnCount, total: errors.length };
  }, [errors]);

  const maxCalibrationTrades = calibration ? Math.max(1, ...calibration.map((b) => b.tradeCount)) : 1;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div>
        <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">
          Confidence Calibration
        </h3>
        <p className="mt-1 font-mono text-[11px] text-ink-faint">
          Closed-trade win rate by AI confidence bucket — a well-calibrated model's win rate should climb
          alongside confidence.
        </p>
        <div className="mt-3 space-y-2">
          {calibrationError && (
            <div className="rounded-md border border-sell/40 bg-sell/10 px-3 py-2 font-mono text-xs text-sell">
              {calibrationError}
            </div>
          )}
          {calibration === null && !calibrationError && (
            <div className="font-mono text-xs text-ink-faint">Loading…</div>
          )}
          {calibration?.length === 0 && (
            <div className="rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-ink-faint">
              No closed trades with recorded confidence yet.
            </div>
          )}
          {calibration?.map((bucket) => (
            <div key={bucket.bucket} className="rounded-lg border border-line bg-panel px-3 py-2.5">
              <div className="flex items-center justify-between font-mono text-xs">
                <span className="text-ink-dim">confidence {bucket.bucket}</span>
                <span className="text-ink-faint">{bucket.tradeCount} trades</span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-void">
                <div
                  className={`h-full rounded-full ${bucket.winRate >= 0.5 ? "bg-profit" : "bg-sell"}`}
                  style={{ width: `${Math.min(100, bucket.winRate * 100)}%` }}
                />
              </div>
              <div className="mt-1 flex items-center justify-between font-mono text-[11px] text-ink-faint">
                <span>win rate {(bucket.winRate * 100).toFixed(0)}%</span>
                <span className={bucket.avgRealizedPnlUsd >= 0 ? "text-profit" : "text-sell"}>
                  avg {bucket.avgRealizedPnlUsd >= 0 ? "+" : ""}
                  {bucket.avgRealizedPnlUsd.toFixed(2)}
                </span>
              </div>
              <div
                className="mt-0.5 h-1 rounded-full bg-turquoise/30"
                style={{ width: `${(bucket.tradeCount / maxCalibrationTrades) * 100}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">
            Risk Gate Rejections (recent decisions)
          </h3>
          <p className="mt-1 font-mono text-[11px] text-ink-faint">
            Which risk gate is blocking entries most often — helps spot a gate that's mis-tuned rather than
            working as intended.
          </p>
          {rejections.total === 0 ? (
            <div className="mt-3 rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-ink-faint">
              No rejected entries in the current feed.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {rejections.byGate.map(([gate, count]) => (
                <div key={gate} className="flex items-center gap-3">
                  <div className="w-40 shrink-0 font-mono text-xs text-ink-dim">{gate}</div>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-void">
                    <div
                      className="h-full rounded-full bg-gold"
                      style={{ width: `${(count / rejections.total) * 100}%` }}
                    />
                  </div>
                  <div className="w-8 shrink-0 text-right font-mono text-xs tabular text-ink-faint">{count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">
            Technical Error Rate
          </h3>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-line bg-panel px-3 py-2.5 text-center">
              <div className="font-mono text-lg font-semibold tabular text-sell">{errorRate.errorCount}</div>
              <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">errors</div>
            </div>
            <div className="rounded-lg border border-line bg-panel px-3 py-2.5 text-center">
              <div className="font-mono text-lg font-semibold tabular text-gold">{errorRate.warnCount}</div>
              <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">warnings</div>
            </div>
            <div className="rounded-lg border border-line bg-panel px-3 py-2.5 text-center">
              <div className="font-mono text-lg font-semibold tabular text-ink">{errorRate.total}</div>
              <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">total (recent)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
